import { useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DateToDateAndTimeFormat } from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import { clearInstalmentToPay, clearPaymentResponse } from '../../../store/slice/paymentSlice';
import { CSPUrlsMap } from '../../../routes/CSPUrlsMap';
import { FeatureFlagContext } from '../../../contextProviders/featureFlagContext';
import { getPaymentErrorMessageMapping } from '../../../helpers/paymentHelper';
import {
  useValidateStateWithOutstandingPremiumMutation,
  useUpdatePremiumRecordMutation
} from '../../../data/premiumsandpayments';
import { SLICES } from '../../../constants/slices';
import { SELECT_PAYMENT, ENTER_CREDIT_CARD, SUMMARY, PCI_PAYMENT_ACTION } from '../../../constants/constants';
import { SESSION_STORAGE_KEYS } from '../../../constants/sessionStorageKeys';
import { INSTALMENT_STATUS, INSTALMENT_STATUS_CODE } from '../../../constants/payment';
import { handleTracking } from '../../../services/gtmService/tracking';

const handlePremiumRecordUpdate = (updatePremiumRecord, payload) => {
  if (!payload) return;
  updatePremiumRecord(payload)
    .unwrap()
    .catch((error) => {
      console.error('Failed to update premium record:', error); // eslint-disable-line no-console
    });
};

export const usePayment = () => {
  const { cspConfiguration, isCspConfigLoading } = useContext(FeatureFlagContext);
  const isPayNowEnabled = cspConfiguration?.IsPayNowEnabled;
  const isLegacyPaymentEnabled = cspConfiguration?.IsLegacyPaymentEnabled || false;
  const isInstalmentProcessingEnabled = cspConfiguration?.IsInstalmentProcessingEnabled || false;
  const [isPopupShowing, setPopupIsShowing] = useState(false);
  const selectedInstalmentToPay = useSelector((state) => state?.[SLICES.PAYMENT].instalmentToPayList);
  const paymentResponse = useSelector((state) => state?.[SLICES.PAYMENT].paymentResponseObj);
  const [activeStep, setActiveStep] = useState(SELECT_PAYMENT);
  const [paymentDetails, setPaymentDetails] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [validateStateWithOutstandingPremium, { data: validateResponse }] =
    useValidateStateWithOutstandingPremiumMutation();
  const [updatePremiumRecord] = useUpdatePremiumRecordMutation();
  const [searchParams] = useSearchParams();
  const [paymentResponseObj, setPaymentResponseObj] = useState({
    SummaryCode: '',
    ResponseMessage: '',
    ReceiptNumber: '',
    TransactionTime: '',
    ResponseCode: '',
    ResponseDescription: ''
  });

  useEffect(() => {
    if (isLegacyPaymentEnabled) {
      if (selectedInstalmentToPay?.activeStep === ENTER_CREDIT_CARD && selectedInstalmentToPay?.transaction) {
        setActiveStep(ENTER_CREDIT_CARD);
        setPaymentDetails(selectedInstalmentToPay.transaction);
      }

      if (paymentResponse?.activeStep === SUMMARY) {
        const {
          SummaryCode: summaryCode,
          ResponseMessage: responseMessage,
          ReceiptNumber: receiptNumber,
          TransactionTime: transactionTime
        } = paymentResponse.payment.responseObject;
        const { totalAmountPaid } = paymentResponse;

        setPaymentResponseObj({
          SummaryCode: summaryCode,
          ResponseMessage: responseMessage,
          ReceiptNumber: receiptNumber,
          TransactionTime: transactionTime,
          TotalAmount: totalAmountPaid
        });
        setActiveStep(SUMMARY);
      }
    }
  }, [
    selectedInstalmentToPay.activeStep,
    paymentResponse,
    selectedInstalmentToPay.transaction,
    isLegacyPaymentEnabled
  ]);

  useEffect(() => {
    if (isCspConfigLoading) return;
    if (typeof isPayNowEnabled !== 'boolean') return;

    setPopupIsShowing(!isPayNowEnabled);
  }, [isPayNowEnabled, isCspConfigLoading]);

  useEffect(() => {
    if (isCspConfigLoading) {
      return;
    }
    if (!isLegacyPaymentEnabled) {
      if (!window.location.search.includes('userState=')) return;

      const queryString = window.location.search;
      const mapFromQuery = (key) => searchParams.get(key) || '';
      setPaymentResponseObj({
        SummaryCode: mapFromQuery('summaryCode'),
        ResponseMessage: getPaymentErrorMessageMapping(mapFromQuery('responseCode')),
        ReceiptNumber: mapFromQuery('receiptNumber'),
        TransactionTime: mapFromQuery('createdDateTime'),
        TotalAmount: parseFloat(mapFromQuery('paymentAmount')) || 0,
        ResponseCode: mapFromQuery('responseCode'),
        ResponseDescription: mapFromQuery('responseDescription')
      });

      validateStateWithOutstandingPremium({
        QueryString: queryString
      });

      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [validateStateWithOutstandingPremium, searchParams, isLegacyPaymentEnabled, isCspConfigLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const createPremiumRecordPayload = (status, statusCode, statusDescription) => {
    const paymentReferenceNumber = sessionStorage.getItem(SESSION_STORAGE_KEYS.PAYMENT_REFERENCE_NUMBER);
    if (
      !paymentReferenceNumber ||
      paymentReferenceNumber === 'null' ||
      paymentReferenceNumber === 'undefined' ||
      paymentReferenceNumber === ''
    ) {
      return null;
    }
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.PAYMENT_REFERENCE_NUMBER);
    return {
      ReferenceNumber: paymentReferenceNumber,
      Status: status,
      StatusCode: statusCode,
      StatusDescription: statusDescription
    };
  };
  useEffect(() => {
    if (isCspConfigLoading) {
      return;
    }

    if (validateResponse && validateResponse?.isSuccessResponse && !isLegacyPaymentEnabled) {
      if (searchParams.get('action') === PCI_PAYMENT_ACTION.PROCESSED) {
        if (validateResponse?.responseObject) {
          setActiveStep(SUMMARY);
          if (isInstalmentProcessingEnabled) {
            const successMessage = `Successful payment from customer service portal. Payment Ref Num: ${
              paymentResponseObj.ReceiptNumber
            }, Date: ${DateToDateAndTimeFormat(new Date(paymentResponseObj.TransactionTime))}, Amount: $${
              paymentResponseObj.TotalAmount
            }`;
            const updatePremiumRecordPayload = createPremiumRecordPayload(
              INSTALMENT_STATUS.COMPLETED,
              INSTALMENT_STATUS_CODE.INFO05,
              successMessage
            );
            handlePremiumRecordUpdate(updatePremiumRecord, updatePremiumRecordPayload);
          }
        } else {
          setActiveStep(SUMMARY);
          if (isInstalmentProcessingEnabled) {
            const unsuccessfulPaymentMessage = `Payment unsuccessful from Customer Service Portal. Response Code: ${paymentResponseObj.ResponseCode}. Response Description: ${paymentResponseObj.ResponseDescription}.`;
            const updatePremiumRecordPayload = createPremiumRecordPayload(
              INSTALMENT_STATUS.ERROR,
              INSTALMENT_STATUS_CODE.INFO02,
              unsuccessfulPaymentMessage
            );
            handlePremiumRecordUpdate(updatePremiumRecord, updatePremiumRecordPayload);
          }
        }
      }

      if (searchParams.get('action') === PCI_PAYMENT_ACTION.CANCELLED) {
        handleTracking('payment_cancelled', { label: 'Pay Now' });

        if (isInstalmentProcessingEnabled) {
          const updatePremiumRecordPayload = createPremiumRecordPayload(
            INSTALMENT_STATUS.ERROR,
            INSTALMENT_STATUS_CODE.INFO01,
            'Cancelled Payment from Customer Service Portal'
          );
          handlePremiumRecordUpdate(updatePremiumRecord, updatePremiumRecordPayload);
        }
      }
    }
  }, [
    validateResponse,
    isLegacyPaymentEnabled,
    isCspConfigLoading,
    isInstalmentProcessingEnabled,
    paymentResponseObj,
    searchParams,
    updatePremiumRecord
  ]);

  const handleCancelButton = () => {
    // Clear selected instalments
    dispatch(clearInstalmentToPay());
    setActiveStep(SELECT_PAYMENT);
  };

  const handleMakeAnotherPaymentButton = () => {
    // Clear selected instalments and payment response
    dispatch(clearInstalmentToPay());
    dispatch(clearPaymentResponse());
    setActiveStep(SELECT_PAYMENT);
  };

  const handleUpdateBankDetails = () => {
    // Clear selected instalments and payment response
    // Redirect to bank details page
    dispatch(clearInstalmentToPay());
    dispatch(clearPaymentResponse());
    navigate(`${CSPUrlsMap.bankDetails.url}`);
    setActiveStep(SELECT_PAYMENT);
  };

  const onClose = () => setPopupIsShowing(false);
  const onBackToDashboard = () => navigate(`${CSPUrlsMap.dashboard.url}`);
  return {
    isPopupShowing,
    onClose,
    onBackToDashboard,
    activeStep,
    isLegacyPaymentEnabled,
    paymentDetails,
    handleCancelButton,
    paymentResponseObj,
    handleMakeAnotherPaymentButton,
    handleUpdateBankDetails
  };
};
